import { CheckboxGroup } from '@base-ui/react/checkbox-group';
import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { ChevronLeft } from 'lucide-react';
import { Accordion } from '@/components/ui/Accordion';
import { Checkbox } from '@/components/ui/Checkbox';
import { HoverTip } from '@/components/ui/HoverTip';
import { ScrollPane } from '@/components/ui/ScrollPane';
import { Shortcut } from '@/components/ui/Shortcut';
import { Slider } from '@/components/ui/Slider';
import { cx } from '@/cva.config';

export interface JobFilters {
  minScore: number;
  query: string;
  sources: string[];
  status:
    | 'all'
    | 'new'
    | 'scored'
    | 'ready'
    | 'approved'
    | 'applied'
    | 'rejected'
    | 'failed';
}

export const SOURCE_OPTIONS = [
  { id: 'working_nomads', label: 'Working Nomads' },
  { id: 'remote100k', label: 'Remote > $100K' },
  { id: 'levelsfyi', label: 'Levels.fyi' },
  { id: 'jobgether', label: 'Jobgether' },
  { id: 'wellfound', label: 'Wellfound' },
  { id: 'linkedin', label: 'LinkedIn' },
] as const;

interface FilterRailProps {
  counts: Record<string, number>;
  filters: JobFilters;
  onToggle: () => void;
  open: boolean;
  setFilters: (updater: (prev: JobFilters) => JobFilters) => void;
}

const STATUS_ITEMS: { id: JobFilters['status']; label: string }[] = [
  { id: 'all', label: 'All jobs' },
  { id: 'ready', label: 'Ready' },
  { id: 'scored', label: 'Scored' },
  { id: 'new', label: 'Unscored' },
  { id: 'approved', label: 'Approved' },
  { id: 'applied', label: 'Applied' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'failed', label: 'Failed' },
];

export const FilterRail = ({
  counts,
  filters,
  onToggle,
  open,
  setFilters,
}: FilterRailProps) => (
  <aside
    className={cx(
      'hidden w-9 shrink-0 flex-col overflow-hidden border-r border-border bg-background transition-[width] duration-250 ease-out lg:flex',
      open && 'w-56',
    )}
  >
    <HoverTip
      align="start"
      className="flex! shrink-0"
      delay={250}
      text={open ? 'Collapse filters  ⌘B' : 'Show filters  ⌘B'}
    >
      <button
        className="flex h-9 w-full shrink-0 items-center gap-2 border-b border-border px-3 transition-colors hover:bg-border/30"
        onClick={onToggle}
        type="button"
      >
        <ChevronLeft
          className={cx(
            'size-3 shrink-0 text-foreground-muted transition-transform duration-200',
            !open && 'rotate-180',
          )}
        />
        <span
          className={cx(
            'flex flex-1 items-center justify-between font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase transition-opacity duration-200',
            open ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <span>Filters</span>
          <Shortcut keys="⌘B" />
        </span>
      </button>
    </HoverTip>

    <div
      className={cx(
        'flex min-h-0 w-56 flex-1 flex-col transition-opacity duration-200',
        open ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <ScrollPane className="flex-1" viewportClassName="p-3">
        <div className="mb-5">
          <p className="mb-1.5 px-2 font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase">
            Pipeline
          </p>
          <RadioGroup
            className="space-y-px"
            onValueChange={(status) =>
              setFilters((f) => ({
                ...f,
                status: status as JobFilters['status'],
              }))
            }
            value={filters.status}
          >
            {STATUS_ITEMS.map((item) => (
              <Radio.Root
                className="group flex w-full cursor-pointer items-center justify-between rounded-md px-2 py-1.5 text-left text-xs lg:text-sm transition-colors data-checked:bg-accent-soft data-checked:text-accent data-unchecked:text-foreground-muted data-unchecked:hover:bg-border/40 data-unchecked:hover:text-foreground"
                key={item.id}
                value={item.id}
              >
                <span>{item.label}</span>
                <span className="font-mono text-2xs lg:text-xs text-subtle tabular-nums">
                  {counts[item.id] ?? 0}
                </span>
              </Radio.Root>
            ))}
          </RadioGroup>
        </div>

        <div className="mb-5">
          <p className="mb-1.5 px-2 font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase">
            Sources
          </p>
          <CheckboxGroup
            className="space-y-px"
            onValueChange={(sources) =>
              setFilters((f) => ({
                ...f,
                sources: sources as JobFilters['sources'],
              }))
            }
            value={filters.sources}
          >
            {SOURCE_OPTIONS.map(({ id, label }) => (
              <label
                className={cx(
                  'group flex w-full cursor-pointer items-center justify-between rounded-md px-2 py-1.5 text-xs lg:text-base transition-colors',
                  filters.sources.includes(id)
                    ? 'bg-accent-soft text-accent'
                    : 'text-foreground-muted hover:bg-border/40 hover:text-foreground',
                )}
                htmlFor={id}
                key={id}
              >
                <span className="flex items-center gap-2">
                  <Checkbox name={id} value={id} />
                  <span className="truncate">{label}</span>
                </span>
                <span className="font-mono text-2xs lg:text-xs text-subtle tabular-nums">
                  {counts[id] ?? 0}
                </span>
              </label>
            ))}
          </CheckboxGroup>
        </div>

        <div>
          <p className="mb-1.5 px-2 font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase">
            Min score
          </p>
          <div className="px-2">
            <div className="flex items-center gap-2">
              <Slider
                className="flex-1"
                max={100}
                min={0}
                onChange={(minScore) => setFilters((f) => ({ ...f, minScore }))}
                step={5}
                value={filters.minScore}
              />
              <span className="w-8 text-right font-mono text-xs lg:text-sm text-foreground tabular-nums">
                {filters.minScore}
              </span>
            </div>
          </div>
        </div>
      </ScrollPane>

      <Accordion
        chevronPosition="right"
        className="border-t border-border"
        title="Keyboard shortcuts"
        triggerClassName="h-8 rounded-none px-3 py-0 font-mono text-2xs lg:text-xs font-normal tracking-wider text-subtle uppercase"
      >
        <ul className="space-y-1.5 px-5 pt-1 pb-3 font-mono text-2xs lg:text-xs text-foreground-muted">
          <li className="flex justify-between">
            <span>Open palette</span>
            <Shortcut keys="⌘K" />
          </li>
          <li className="flex justify-between">
            <span>Toggle filters</span>
            <Shortcut keys="⌘B" />
          </li>
          <li className="flex justify-between">
            <span>Next job</span>
            <Shortcut keys="J" />
          </li>
          <li className="flex justify-between">
            <span>Prev job</span>
            <Shortcut keys="K" />
          </li>
          <li className="flex justify-between">
            <span>Approve</span>
            <Shortcut keys="A" />
          </li>
          <li className="flex justify-between">
            <span>Reject</span>
            <Shortcut keys="X" />
          </li>
          <li className="flex justify-between">
            <span>Toggle density</span>
            <Shortcut keys="D" />
          </li>
        </ul>
      </Accordion>
    </div>
  </aside>
);
